import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Globe, Languages, Banknote, Users, Leaf, MapPin } from "lucide-react";
import Navbar from "../components/Navbar";
import { getCultureData } from "../data/cultureData";

const LORA  = { fontFamily: "'Lora', Georgia, serif" };
const SERIF = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
const LS_TRIP = "voyage_r_active_trip";

function getDestination() {
  try {
    const trip = JSON.parse(localStorage.getItem(LS_TRIP) || "null");
    return trip?.destination || null;
  } catch { return null; }
}

function SectionHeader({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex items-start gap-3 mb-4">
      <div
        className="w-9 h-9 flex-shrink-0 bg-[#F1EBE1] border border-[#DCD4C6] flex items-center justify-center"
        style={{ borderRadius: "2px" }}
      >
        <Icon size={16} color="#C17F59" strokeWidth={1.5} />
      </div>
      <div>
        <h2 className="text-2xl leading-tight" style={{ ...SERIF, fontWeight: 600, color: "#2B2623" }}>
          {title}
        </h2>
        {subtitle && (
          <p className="text-xs text-[#9A8F86] italic mt-0.5" style={LORA}>
            {subtitle}
          </p>
        )}
      </div>
    </div>
  );
}

// ── No destination placeholder ────────────────────────────────────────────────
function NoTrip() {
  return (
    <div className="min-h-screen" style={{ background: "#F9F6F0" }}>
      <Navbar />
      <div className="max-w-md mx-auto px-6 pt-20 pb-16 text-center">
        <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center border-2 border-[#DCD4C6] rounded-full">
          <Globe size={28} color="#C17F59" strokeWidth={1.5} />
        </div>
        <h1
          className="text-4xl mb-3"
          style={{ ...SERIF, fontWeight: 600, color: "#2B2623", fontStyle: "italic" }}
        >
          Guide de Survie Local
        </h1>
        <p className="text-sm text-[#9A8F86] italic mb-2" style={LORA}>
          Aucun voyage actif détecté.
        </p>
        <p className="text-sm text-[#5C554E] mb-8 leading-relaxed" style={LORA}>
          Planifiez votre voyage pour obtenir votre guide culturel personnalisé —
          lexique, pourboires, coutumes et règles de la faune adaptés à votre destination.
        </p>
        <Link
          to="/plan"
          className="inline-flex items-center gap-2 px-6 py-3 bg-[#C17F59] text-[#F9F6F0] text-sm hover:bg-[#A86A46] transition-colors"
          style={{ ...LORA, borderRadius: "2px", fontWeight: 600 }}
          data-testid="culture-cta-plan"
        >
          <MapPin size={15} strokeWidth={1.5} />
          Planifier mon voyage
        </Link>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function CulturePage() {
  const navigate    = useNavigate();
  const destination = getDestination();
  const data        = getCultureData(destination || "");

  if (!destination) return <NoTrip />;

  return (
    <div className="min-h-screen" style={{ background: "#F9F6F0" }}>
      <Navbar />

      <div className="max-w-2xl mx-auto px-4 pt-6 pb-24">

        {/* ── Page header ── */}
        <div className="flex items-start gap-3 mb-8">
          <button
            onClick={() => navigate(-1)}
            className="text-[#9A8F86] hover:text-[#2B2623] transition-colors mt-1 flex-shrink-0"
            data-testid="btn-back"
          >
            <ChevronLeft size={20} strokeWidth={1.5} />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-2xl" role="img" aria-label="drapeau">{data.flag}</span>
              <h1
                className="text-4xl"
                style={{ ...SERIF, fontWeight: 600, color: "#2B2623", fontStyle: "italic" }}
              >
                Guide de Survie Local
              </h1>
            </div>
            <p className="text-xs text-[#9A8F86] mt-0.5 flex items-center gap-1" style={LORA}>
              <MapPin size={10} strokeWidth={2} />
              {destination} · Fonctionne hors-ligne
            </p>
          </div>
        </div>

        {/* ══════════════════════════════════════════
            SECTION 1 — Lexique
        ═══════════════════════════════════════════ */}
        <section className="mb-10" data-testid="section-lexique">
          <SectionHeader
            icon={Languages}
            title="Les Mots Magiques"
            subtitle="5 expressions indispensables · prononciation simplifiée"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {data.lexique.map((item, i) => (
              <div
                key={i}
                className="bg-white border border-[#DCD4C6] p-4"
                style={{ borderRadius: "3px" }}
                data-testid={`lexique-item-${i}`}
              >
                <p
                  className="text-xl leading-tight mb-1"
                  style={{ ...SERIF, fontWeight: 600, color: "#2B2623" }}
                >
                  {item.word}
                </p>
                <p className="text-sm text-[#5C554E] mb-2" style={LORA}>
                  {item.translation}
                </p>
                <p className="text-xs italic mb-2" style={{ ...LORA, color: "#C17F59" }}>
                  [{item.phonetic}]
                </p>
                <span
                  className="inline-block text-xs bg-[#F1EBE1] text-[#8B5E34] px-2 py-0.5"
                  style={{ borderRadius: "2px", ...LORA }}
                >
                  {item.lang}
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* ══════════════════════════════════════════
            SECTION 2 — Pourboires
        ═══════════════════════════════════════════ */}
        <section className="mb-10" data-testid="section-pourboires">
          <SectionHeader
            icon={Banknote}
            title="Le Guide du Pourboire"
            subtitle={
              data.currency
                ? `Montants en ${data.currency} · Optionnel sauf indication contraire`
                : "Recommandations internationales"
            }
          />
          <div
            className="border border-[#DCD4C6] bg-white overflow-hidden"
            style={{ borderRadius: "3px" }}
          >
            {data.pourboires.map((tip, i) => (
              <div
                key={i}
                className={`flex items-center justify-between px-4 py-3.5 gap-4 ${
                  i < data.pourboires.length - 1 ? "border-b border-[#F1EBE1]" : ""
                }`}
                data-testid={`tip-row-${i}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#2B2623]" style={LORA}>
                    {tip.service}
                  </p>
                  {tip.note && (
                    <p className="text-xs text-[#9A8F86] italic mt-0.5" style={LORA}>
                      {tip.note}
                    </p>
                  )}
                </div>
                <span
                  className="flex-shrink-0 text-sm font-semibold text-[#C17F59] bg-[#F9F6F0] border border-[#DCD4C6] px-3 py-1"
                  style={{ ...LORA, borderRadius: "2px", whiteSpace: "nowrap" }}
                  data-testid={`tip-amount-${i}`}
                >
                  {tip.amount}
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* ══════════════════════════════════════════
            SECTION 3 — Coutumes
        ═══════════════════════════════════════════ */}
        <section className="mb-10" data-testid="section-coutumes">
          <SectionHeader
            icon={Users}
            title="Savoir-Vivre & Coutumes"
            subtitle="Les règles de politesse pour éviter les faux pas"
          />
          <div className="space-y-2">
            {data.coutumes.map((rule, i) => (
              <div
                key={i}
                className="flex items-start gap-3 bg-white border border-[#DCD4C6] px-4 py-3"
                style={{ borderRadius: "3px" }}
                data-testid={`coutume-item-${i}`}
              >
                <span
                  className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-xs font-semibold text-[#C17F59] bg-[#F1EBE1] border border-[#DCD4C6]"
                  style={{ ...SERIF, borderRadius: "50%", lineHeight: 1 }}
                >
                  {i + 1}
                </span>
                <p className="text-sm text-[#5C554E] leading-relaxed" style={LORA}>
                  {rule}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* ══════════════════════════════════════════
            SECTION 4 — Nature & Faune
        ═══════════════════════════════════════════ */}
        <section className="mb-8" data-testid="section-nature">
          <SectionHeader
            icon={Leaf}
            title="Respect de la Nature & Faune"
            subtitle="Consignes strictes pour protéger l'écosystème local"
          />
          <div className="space-y-2">
            {data.nature.map((rule, i) => (
              <div
                key={i}
                className="flex items-start gap-3 bg-white border border-[#DCD4C6] px-4 py-3"
                style={{ borderRadius: "3px", borderLeft: "3px solid #C17F59" }}
                data-testid={`nature-item-${i}`}
              >
                <p className="text-sm text-[#5C554E] leading-relaxed" style={LORA}>
                  {rule}
                </p>
              </div>
            ))}
          </div>
          <p className="text-xs text-[#9A8F86] italic text-center mt-4" style={LORA}>
            Ces consignes sont disponibles hors-ligne · Pensez à les consulter avant chaque excursion.
          </p>
        </section>

      </div>
    </div>
  );
}
